import React from 'react';

/**
 * ONE authoritative pill per booking card, used identically across
 * Today, Day, By-barber and Week views. In-flow (never absolute) so
 * it can't escape its card. Cancelled trumps everything else — the
 * caller never has to render two pills stacked in the same spot.
 *
 * Copy is derived from the same signals the diary already carries on
 * the booking row: status, payment_status, deposit_paid, paid_in_full,
 * balance_paid_method, deposit_amount, and the joined service.
 */
const CANCEL_CLASSES = new Set(['cancelled', 'no_show']);

export const derivePill = (b) => {
  // Cancelled trumps payment state — one pill only, no stacking.
  if (b.status === 'cancelled') return { cls: 'failing', label: 'Cancelled' };
  if (b.status === 'no_show')   return { cls: 'failing', label: 'No show' };

  const ps = b.payment_status;
  if (b.paid_in_full) {
    const m = b.balance_paid_method;
    return { cls: 'paid', label: m ? `Paid in full · ${m === 'cash' ? 'Cash' : 'Card'}` : 'Paid in full' };
  }
  if (ps === 'failed' || ps === 'cancelled') return { cls: 'failing', label: 'Payment failed' };
  if (b.deposit_paid) {
    const price = Number(b.service?.price ?? 0);
    const deposit = Number(b.deposit_amount ?? 0);
    const owing = Math.max(0, price - deposit);
    return { cls: 'partial', label: owing > 0 ? `Deposit paid · £${owing.toFixed(0)} owing` : 'Deposit paid' };
  }
  if (ps === 'processing') return { cls: 'pending', label: 'Pending' };
  if (Number(b.deposit_amount ?? 0) === 0 || b.service?.deposit_mode === 'none') {
    return { cls: 'notreq', label: 'Pay at chair' };
  }
  return { cls: 'unpaid', label: 'Not paid' };
};

export const BookingPill = ({ booking, className = '', testIdSuffix = '' }) => {
  const pill = derivePill(booking);
  return (
    <span
      className={`bpill ${pill.cls} ${className}`}
      data-testid={testIdSuffix ? `booking-pill-${testIdSuffix}` : 'booking-pill'}
      data-cancelled={CANCEL_CLASSES.has(booking.status) ? 'true' : 'false'}
      title={pill.label}
    >
      {pill.label}
    </span>
  );
};

export default BookingPill;
